import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowUpOutlined,
    CheckOutlined,
    DownloadOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FolderOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    PushpinFilled,
    ShoppingOutlined,
} from "@ant-design/icons";
import { Button, Empty, Modal, Spin, Typography, message } from "antd";
import SelectionPhotoPreview from "./SelectionPhotoPreview";
import DeliveryLazyImage from "../delivery/DeliveryLazyImage";
import DeliveryViewToggle from "../delivery/DeliveryViewToggle";
import MobileBatchSavePanel from "../delivery/MobileBatchSavePanel";
import ScrollNavigator from "../delivery/ScrollNavigator";
import SocialMediaCards from "../delivery/SocialMediaCards";
import FeedbackPrompt from "../feedback/FeedbackPrompt";
import { useTranslation } from "../../i18n";
import { useIsMobileDeliveryDevice } from "../../utils/device";
import { downloadDeliveryFile } from "../../utils/deliveryDownload";
import {
    getFileDownloadUrl,
    getFileThumbnailUrl,
    getDownloadAllUrl,
    isDeliveryApiConfigured,
    isFolderItem,
    isImageItem,
    listDeliveryFolder,
} from "../../utils/deliveryApi";
import { toFolderPrefix } from "../../utils/deliveryPath";
import { getDeliveryPreviewKind } from "../../utils/deliveryFileTypes";

const { Text } = Typography;

function renderFileIcon(item) {
    switch (getDeliveryPreviewKind(item)) {
        case "pdf":
            return <FilePdfOutlined className="delivery-browser__file-icon delivery-browser__file-icon--pdf" />;
        case "video":
            return <PlayCircleOutlined className="delivery-browser__file-icon delivery-browser__file-icon--video" />;
        case "text":
            return <FileTextOutlined className="delivery-browser__file-icon" />;
        default:
            return <FileOutlined className="delivery-browser__file-icon" />;
    }
}

function getFolderPathPrefix(breadcrumbs) {
    if (breadcrumbs.length <= 1) {
        return "";
    }
    return `${breadcrumbs
        .slice(1)
        .map((crumb) => crumb.name)
        .join("/")}/`;
}

// 选片相册浏览器（独立于 delivery）：浏览 + 下载 + 加入选片车。
// props:
//   albumPath, albumName
//   isPicked(id) => bool
//   onTogglePick(pickEntry) 切换某张照片的选中状态
//   pickedCount 已选数量
//   onViewCart() 前往选片车
function SelectionFolderBrowser({
    albumPath,
    albumName,
    downloadAll,
    linkId,
    sourceType = "selection",
    isPicked,
    onTogglePick,
    getNote,
    onUpdateNote,
    pickedCount,
    onViewCart,
}) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useIsMobileDeliveryDevice();
    const [breadcrumbs, setBreadcrumbs] = useState([
        { id: albumPath, name: albumName || t("selection.gallery.defaultAlbumName") },
    ]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [notFound, setNotFound] = useState(false);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [viewMode, setViewMode] = useState("waterfall");
    const [mobileBatchSaveFiles, setMobileBatchSaveFiles] = useState(null);
    const [downloadAllMobileOpen, setDownloadAllMobileOpen] = useState(false);

    const currentFolder = breadcrumbs[breadcrumbs.length - 1];
    const pathPrefix = useMemo(() => getFolderPathPrefix(breadcrumbs), [breadcrumbs]);
    const imageItems = useMemo(
        () => items.filter((item) => isImageItem(item.mimeType)),
        [items]
    );

    const buildPickEntry = useCallback(
        (file) => ({
            id: file.id,
            name: file.name,
            path: `${pathPrefix}${file.name}`,
            thumbnailUrl: getFileThumbnailUrl(file) || null,
            downloadUrl: getFileDownloadUrl(file) || null,
        }),
        [pathPrefix]
    );

    const handleTogglePick = useCallback(
        (file) => {
            onTogglePick(buildPickEntry(file));
        },
        [buildPickEntry, onTogglePick]
    );

    const handleNoteChange = useCallback(
        (file, value) => {
            onUpdateNote?.(buildPickEntry(file), value);
        },
        [buildPickEntry, onUpdateNote]
    );

    const loadFolder = useCallback(
        async (folderId, { append = false, pageToken = null } = {}) => {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setError("");
                setErrorCode("");
                setNotFound(false);
            }

            try {
                const data = await listDeliveryFolder(toFolderPrefix(folderId), {
                    rootPrefix: albumPath,
                    pageToken,
                });
                setItems((prev) => (append ? [...prev, ...data.files] : data.files));
                setNextPageToken(data.nextPageToken || null);
            } catch (err) {
                if (!append) {
                    setItems([]);
                }
                if (err.code === "MISSING_API") {
                    setErrorCode("MISSING_API");
                    setError(err.message || t("selection.gallery.albumLoadFailed"));
                } else if (err.status === 404) {
                    setNotFound(true);
                    setErrorCode("ALBUM_NOT_FOUND");
                    setError(t("selection.gallery.albumNotFound"));
                } else {
                    setErrorCode("ALBUM_LOAD_FAILED");
                    setError(t("selection.gallery.albumLoadFailed"));
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [albumPath, t]
    );

    useEffect(() => {
        loadFolder(currentFolder.id);
    }, [currentFolder.id, loadFolder]);

    const handleOpenFolder = useCallback((folder) => {
        setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    }, []);

    const handleBackToParent = useCallback(() => {
        setBreadcrumbs((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    }, []);

    const canGoUp = breadcrumbs.length > 1;

    const handleDownloadFile = useCallback(
        async (file) => {
            // 手机端图片：与 delivery 一致，弹出长按保存页面（直接下载在移动端常失败）。
            if (isMobile && isImageItem(file.mimeType)) {
                setMobileBatchSaveFiles([file]);
                return;
            }

            const hide = message.loading(`${t("selection.gallery.downloading")}…`, 0);
            try {
                await downloadDeliveryFile(file);
            } catch (err) {
                message.error(err.message || t("selection.gallery.downloadFailed"));
            } finally {
                hide();
            }
        },
        [isMobile, t]
    );

    const handleLoadMore = useCallback(() => {
        if (!nextPageToken || loadingMore) {
            return;
        }
        loadFolder(currentFolder.id, { append: true, pageToken: nextPageToken });
    }, [currentFolder.id, loadFolder, loadingMore, nextPageToken]);

    const handleDownloadAll = useCallback(() => {
        if (isMobile) {
            setDownloadAllMobileOpen(true);
            return;
        }

        const url = getDownloadAllUrl(downloadAll, albumPath);
        if (!url) {
            message.error(t("delivery.actions.downloadAllFailed"));
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
    }, [albumPath, downloadAll, isMobile, t]);

    const handleDownloadAllAnyway = useCallback(() => {
        const url = getDownloadAllUrl(downloadAll, albumPath);
        if (!url) {
            message.error(t("delivery.actions.downloadAllFailed"));
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
        setDownloadAllMobileOpen(false);
    }, [albumPath, downloadAll, t]);

    const openFilePreview = useCallback((file) => {
        setPreviewFile(file);
    }, []);

    if (loading) {
        return (
            <div className="delivery-browser delivery-browser--centered">
                <Spin size="large" />
                <p>{t("selection.gallery.loadingAlbum")}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="delivery-browser delivery-browser--centered">
                <p className="delivery-album-error__text">
                    {notFound
                        ? t("selection.gallery.albumNotFound")
                        : t("selection.gallery.albumLoadFailed")}
                </p>
                {errorCode === "MISSING_API" && !isDeliveryApiConfigured() ? (
                    <Text type="secondary" className="delivery-browser__hint">
                        {t("selection.gallery.missingApiHint")}
                    </Text>
                ) : notFound ? null : (
                    <Text type="secondary" className="delivery-browser__hint">
                        {t("common.networkTroubleshootHint")}
                    </Text>
                )}
                <div className="delivery-album-error__actions">
                    <Button type="primary" onClick={() => navigate("/contact")}>
                        {t("selection.gallery.contactPhotographer")}
                    </Button>
                    <Button onClick={() => loadFolder(currentFolder.id)}>
                        {t("selection.gallery.retry")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="delivery-browser selection-browser">
            <div className="delivery-browser__toolbar">
                {canGoUp && (
                    <button
                        type="button"
                        className="delivery-browser__back"
                        onClick={handleBackToParent}
                        title={t("selection.gallery.backToParentTitle")}
                    >
                        <ArrowUpOutlined />
                        <span className="delivery-browser__back-label">
                            {t("selection.gallery.backToParent")}
                        </span>
                    </button>
                )}
                <span className="delivery-browser__current" title={currentFolder.name}>
                    {currentFolder.name}
                </span>
                <div className="delivery-browser__toolbar-actions">
                    <DeliveryViewToggle mode={viewMode} onChange={setViewMode} />
                    {downloadAll && (
                        <Button
                            type="default"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadAll}
                        >
                            {t("selection.gallery.downloadAllPhotos")}
                        </Button>
                    )}
                    <Button
                        type="default"
                        size="small"
                        icon={<ShoppingOutlined />}
                        onClick={onViewCart}
                    >
                        {t("selection.gallery.viewCart")} ({pickedCount})
                    </Button>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="delivery-browser--centered">
                    <Empty description={t("selection.gallery.emptyFolder")} />
                </div>
            ) : (
                <>
                    <div
                        className={`delivery-browser__grid${
                            viewMode === "waterfall" ? " delivery-browser__grid--waterfall" : ""
                        }`}
                    >
                        {items.map((item) => {
                            if (isFolderItem(item.mimeType)) {
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className="delivery-browser__item delivery-browser__item--folder"
                                        onClick={() => handleOpenFolder(item)}
                                    >
                                        <FolderOutlined className="delivery-browser__folder-icon" />
                                        <span className="delivery-browser__name">{item.name}</span>
                                    </button>
                                );
                            }

                            const thumbnail = getFileThumbnailUrl(item);

                            if (isImageItem(item.mimeType)) {
                                const picked = isPicked(item.id);
                                return (
                                    <div
                                        key={item.id}
                                        className={`delivery-browser__item delivery-browser__item--image${
                                            picked ? " selection-browser__item--picked" : ""
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className={`selection-browser__pick-btn${
                                                picked ? " selection-browser__pick-btn--on" : ""
                                            }`}
                                            aria-label={
                                                picked
                                                    ? t("selection.gallery.removePick")
                                                    : t("selection.gallery.addPick")
                                            }
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleTogglePick(item);
                                            }}
                                        >
                                            {picked ? <CheckOutlined /> : <PlusOutlined />}
                                        </button>
                                        {item.pinned && (
                                            <PushpinFilled
                                                className="selection-browser__pin-badge"
                                                aria-label={t("selection.gallery.pinned")}
                                                title={t("selection.gallery.pinned")}
                                            />
                                        )}
                                        <DeliveryLazyImage
                                            src={thumbnail}
                                            alt={item.name}
                                            layout={viewMode}
                                            className="delivery-browser__thumb"
                                            onClick={() => openFilePreview(item)}
                                        />
                                        {viewMode === "grid" && (
                                            <span className="delivery-browser__name">{item.name}</span>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className="delivery-browser__item delivery-browser__item--file"
                                    onClick={() => handleDownloadFile(item)}
                                >
                                    {thumbnail ? (
                                        <img
                                            src={thumbnail}
                                            alt=""
                                            className="delivery-browser__thumb"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    ) : (
                                        renderFileIcon(item)
                                    )}
                                    <span className="delivery-browser__name">{item.name}</span>
                                </button>
                            );
                        })}
                    </div>

                    {nextPageToken && (
                        <div className="delivery-browser__more">
                            <Button loading={loadingMore} onClick={handleLoadMore}>
                                {t("selection.gallery.loadMore")}
                            </Button>
                        </div>
                    )}

                    <FeedbackPrompt
                        sourceType={sourceType}
                        linkId={linkId}
                        albumPath={albumPath}
                        albumName={albumName || t("selection.gallery.defaultAlbumName")}
                    />
                    <div className="delivery-browser__social">
                        <SocialMediaCards />
                    </div>
                </>
            )}

            <SelectionPhotoPreview
                file={previewFile}
                open={Boolean(previewFile)}
                picked={previewFile ? isPicked(previewFile.id) : false}
                note={previewFile && getNote ? getNote(previewFile.id) : ""}
                onClose={() => setPreviewFile(null)}
                onDownload={handleDownloadFile}
                onTogglePick={handleTogglePick}
                onNoteChange={onUpdateNote ? handleNoteChange : undefined}
                files={imageItems}
                onNavigate={setPreviewFile}
            />

            <MobileBatchSavePanel
                files={mobileBatchSaveFiles}
                open={Boolean(mobileBatchSaveFiles?.length)}
                onClose={() => setMobileBatchSaveFiles(null)}
            />

            <Modal
                open={downloadAllMobileOpen}
                title={t("delivery.actions.downloadAllMobileTitle")}
                onCancel={() => setDownloadAllMobileOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setDownloadAllMobileOpen(false)}>
                        {t("delivery.actions.close")}
                    </Button>,
                    <Button key="continue" type="primary" danger onClick={handleDownloadAllAnyway}>
                        {t("delivery.actions.downloadAllMobileContinue")}
                    </Button>,
                ]}
                className="delivery-browser__modal"
                centered
                destroyOnClose
            >
                <p>{t("delivery.actions.downloadAllMobileBody")}</p>
            </Modal>

            <div className="selection-browser__bar">
                <Text className="selection-browser__bar-count">
                    {t("selection.gallery.selectedCount", { count: pickedCount })}
                </Text>
                <Button type="primary" icon={<ShoppingOutlined />} onClick={onViewCart}>
                    {t("selection.gallery.goToCart")}
                </Button>
            </div>

            <div className="selection-browser__bar-spacer" />

            <ScrollNavigator />
        </div>
    );
}

export default SelectionFolderBrowser;
